'use client';

import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import type { ProjectRole } from '../lib/api/projectPermissions';

export type ProjectPermissionLabels = {
  /** built-in 역할은 서버가 영어 이름을 내려주므로 `builtInKey`로 번역한다. custom은 사용자가 지은 이름 그대로. */
  roleName: (role: Pick<ProjectRole, 'name' | 'builtInKey'>) => string;
  roleDescription: (role: Pick<ProjectRole, 'description' | 'builtInKey'>) => string | null;
  featureLabel: (feature: string) => string;
  actionLabel: (action: string) => string;
};

/**
 * permission 카탈로그·역할의 사용자 노출 라벨.
 *
 * 서버는 `codeReviews` / `write` 같은 원문 키와 영어 built-in 이름을 내려준다. 그대로 그리면 한국어
 * 화면에 원문이 그대로 노출되므로 이 훅에서만 번역한다.
 *
 * 키가 없으면 원문으로 폴백한다. 서버가 permission을 먼저 추가하고 i18n이 뒤따르는 순서가 정상이라,
 * 그 사이에 화면이 깨지는 대신 영문 키라도 보이는 편이 낫다.
 */
export const useProjectPermissionLabels = (): ProjectPermissionLabels => {
  const tPermissions = useTranslations('projects.permissions');
  const tRoles = useTranslations('projects.roles');

  const featureLabel = useCallback(
    (feature: string) => (tPermissions.has(`feature.${feature}`) ? tPermissions(`feature.${feature}`) : feature),
    [tPermissions],
  );

  const actionLabel = useCallback(
    (action: string) => (tPermissions.has(`action.${action}`) ? tPermissions(`action.${action}`) : action),
    [tPermissions],
  );

  const roleName = useCallback(
    (role: Pick<ProjectRole, 'name' | 'builtInKey'>) => {
      if (!role.builtInKey) return role.name;
      const key = `builtInName.${role.builtInKey}`;
      return tRoles.has(key) ? tRoles(key) : role.name;
    },
    [tRoles],
  );

  const roleDescription = useCallback(
    (role: Pick<ProjectRole, 'description' | 'builtInKey'>) => {
      if (!role.builtInKey) return role.description;
      const key = `builtInDescription.${role.builtInKey}`;
      return tRoles.has(key) ? tRoles(key) : role.description;
    },
    [tRoles],
  );

  return { roleName, roleDescription, featureLabel, actionLabel };
};
